import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Calendar, ArrowLeft, Building2 } from 'lucide-react';
import { TENANT } from '../../config';
import { formatPhone } from '../../utils/formatPhone';

interface JoinTheHiveFormProps {
  onSubmit?: (data: FormData) => void;
  onBack?: () => void;
  embedded?: boolean;
}

export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accreditedInvestor: string;
  newsUpdates: boolean;
}

export function JoinTheHiveForm({ onSubmit, onBack, embedded = false }: JoinTheHiveFormProps) {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [accreditedInvestor, setAccreditedInvestor] = useState('');
  const [newsUpdates, setNewsUpdates] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // No external scheduler is wired yet. Booking routes through the in-app
  // calendar. When a real Calendly link exists, set REACT_APP_CALENDLY_URL
  // and it takes precedence automatically.
  const calendlyUrl = (process.env.REACT_APP_CALENDLY_URL || '').trim();

  const isRedex = TENANT.id === 'redex';
  const headingJoin = TENANT.id === 'redex' ? 'Request access' : 'Join the Hive';
  const headingThanks = TENANT.id === 'redex' ? 'Thanks — we received your request.' : 'Thanks for joining the hive!';
  const platformName = TENANT.branding.name;

  const validateForm = () => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!firstName.trim()) newErrors.firstName = 'Please enter your first name.';
    if (!lastName.trim()) newErrors.lastName = 'Please enter your last name.';
    if (!email.trim()) {
      newErrors.email = 'Please enter your email.';
    } else if (!email.includes('@') || !email.includes('.')) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!phone.trim()) newErrors.phone = 'Please enter your phone number.';
    if (!accreditedInvestor) newErrors.accreditedInvestor = 'Please select an option.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const formData: FormData = {
      firstName,
      lastName,
      email,
      phone,
      accreditedInvestor,
      newsUpdates,
    };

    onSubmit?.(formData);
    setSubmitted(true);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleScheduleCall = () => {
    if (calendlyUrl) {
      window.open(calendlyUrl, '_blank');
      return;
    }

    // Route through the in-app calendar (Investor Relations onboarding call).
    navigate('/portal/onboarding-call?source=join-the-hive');
  };

  const successContent = (
    <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
      {/* Left Side - Success Message */}
      <div className="space-y-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
          <svg
            className="w-8 h-8 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 className={embedded ? "text-3xl sm:text-4xl md:text-5xl font-bold" : "text-4xl md:text-5xl tracking-tight"}>
          {headingThanks}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          We've received your information and someone from our team will be in touch with you soon to discuss next steps.
        </p>
        {!embedded && (
          <p className="text-muted-foreground">
            In the meantime, feel free to schedule a call with our team to learn more about how <span className="font-semibold text-foreground">{platformName}</span> can help.
          </p>
        )}
      </div>

      {/* Right Side - Schedule Call */}
      <div className={embedded ? "bg-muted border border-border rounded-2xl p-8 space-y-6" : "bg-card border border-border rounded-2xl p-8 space-y-6"}>
        <div className="space-y-3">
          <h3 className={embedded ? "text-2xl font-bold" : "text-2xl"}>Schedule a call</h3>
          <p className="text-muted-foreground">
            Talk to our team about your goals.
          </p>
        </div>

        <Button
          onClick={handleScheduleCall}
          size="lg"
          className="w-full"
        >
          <Calendar className="w-5 h-5 mr-2" />
          Book a time
        </Button>

        <div className="space-y-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>30-minute consultation</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>No obligation</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (submitted) {
    if (embedded) {
      return successContent;
    }

    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        {onBack && (
          <header className="border-b border-border bg-card sticky top-0 z-50 backdrop-blur-sm bg-card/80">
            <div className="container mx-auto px-4 py-3 md:py-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={onBack}
                  className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-primary" />
                  <span className="font-medium">{platformName}</span>
                </div>

                <div className="w-8" /> {/* Spacer for alignment */}
              </div>
            </div>
          </header>
        )}

        <section className="py-12 md:py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {successContent}
            </div>
          </div>
        </section>
      </div>
    );
  }

  const formContent = (
    <div className="grid md:grid-cols-2 gap-8 md:gap-12">
      {/* Left Side - Content */}
      <div className="space-y-6">
        <h1 className={embedded ? "text-3xl sm:text-4xl md:text-5xl font-bold" : "text-4xl md:text-5xl tracking-tight"}>
          {headingJoin}
        </h1>
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Start building with <span className="font-semibold">{platformName}</span>
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Complete this short questionnaire and someone from our team will be in touch with you soon!
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name Fields */}
        <div>
          <label className="block text-sm text-muted-foreground mb-2">
            Name
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                className={`w-full px-4 py-3 rounded-lg border ${embedded ? 'bg-background' : 'bg-background'} focus:outline-none focus:ring-2 transition-all ${
                  errors.firstName
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-border focus:ring-primary focus:border-transparent'
                }`}
              />
              {errors.firstName && (
                <p className="mt-1.5 text-sm text-red-500">{errors.firstName}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                className={`w-full px-4 py-3 rounded-lg border ${embedded ? 'bg-background' : 'bg-background'} focus:outline-none focus:ring-2 transition-all ${
                  errors.lastName
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-border focus:ring-primary focus:border-transparent'
                }`}
              />
              {errors.lastName && (
                <p className="mt-1.5 text-sm text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm text-muted-foreground mb-2">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            className={`w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 transition-all ${
              errors.email
                ? 'border-red-500 focus:ring-red-500'
                : 'border-border focus:ring-primary focus:border-transparent'
            }`}
          />
          {errors.email && (
            <p className="mt-1.5 text-sm text-red-500">{errors.email}</p>
          )}
        </div>

        {/* Newsletter Signup */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={newsUpdates}
              onChange={(e) => setNewsUpdates(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-2 border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
            />
            <span className="text-sm text-muted-foreground">
              Sign up for news and updates
            </span>
          </label>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm text-muted-foreground mb-2">
            Phone
          </label>
          <input
            type="tel"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="(555) 123-4567"
            className={`w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 transition-all ${
              errors.phone
                ? 'border-red-500 focus:ring-red-500'
                : 'border-border focus:ring-primary focus:border-transparent'
            }`}
          />
          {errors.phone && (
            <p className="mt-1.5 text-sm text-red-500">{errors.phone}</p>
          )}
        </div>

        {/* Accredited Investor */}
        <div>
          <label className="block text-sm text-muted-foreground mb-2">
            {isRedex ? 'What are you looking to do?' : 'Are you an accredited investor?'}
          </label>
          {!isRedex && (
            <p className="text-xs text-muted-foreground mb-3">
              Required for SEC Compliance: e.g. Annual income over $200k ($300k filing jointly) or $1M+ in assets
            </p>
          )}
          <select
            value={accreditedInvestor}
            onChange={(e) => setAccreditedInvestor(e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 transition-all ${
              errors.accreditedInvestor
                ? 'border-red-500 focus:ring-red-500'
                : 'border-border focus:ring-primary focus:border-transparent'
            }`}
          >
            <option value="">Select an option</option>
            {isRedex ? (
              <>
                <option value="funds">Deploy / allocate funds</option>
                <option value="assets">List / manage assets</option>
                <option value="invoices">Run invoice engagements</option>
                <option value="other">Other / not sure</option>
              </>
            ) : (
              <>
                <option value="yes">Yes, I am an accredited investor</option>
                <option value="no">No, I am not an accredited investor</option>
                <option value="unsure">I'm not sure</option>
              </>
            )}
          </select>
          {errors.accreditedInvestor && (
            <p className="mt-1.5 text-sm text-red-500">{errors.accreditedInvestor}</p>
          )}
        </div>

        {/* Submit Button */}
        <Button type="submit" size="lg" className="w-full font-semibold">
          Submit
        </Button>
      </form>
    </div>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      {onBack && (
        <header className="border-b border-border bg-card sticky top-0 z-50 backdrop-blur-sm bg-card/80">
          <div className="container mx-auto px-4 py-3 md:py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-2">
                <Building2 className="w-6 h-6 text-primary" />
                <span className="font-medium">{platformName}</span>
              </div>

              <div className="w-8" /> {/* Spacer for alignment */}
            </div>
          </div>
        </header>
      )}

      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {formContent}
          </div>
        </div>
      </section>
    </div>
  );
}